import { useMemo } from 'react';

interface Bet {
  id: string;
  result: 'won' | 'lost' | 'pending';
  profit: number;
  stake: number;
  odds: number;
  bookmaker: string;
  date: string;
}

interface LoginStreak {
  current_streak: number;
  longest_streak: number;
  total_logins: number;
}

interface Achievement {
  id: string;
  label: string;
  description: string;
  icon: string;
  color: string;
  unlocked: boolean;
  progress?: number;
  progressMax?: number;
}

interface AchievementBadgesProps {
  bets: Bet[];
  streak: LoginStreak | null;
  postsCount: number;
}

export default function AchievementBadges({ bets, streak, postsCount }: AchievementBadgesProps) {
  const achievements = useMemo<Achievement[]>(() => {
    const closedBets = bets.filter((b) => b.result !== 'pending');
    const wonBets = closedBets.filter((b) => b.result === 'won');
    const totalStake = closedBets.reduce((acc, b) => acc + b.stake, 0);
    const totalProfit = bets.reduce((acc, b) => acc + b.profit, 0);
    const roi = totalStake > 0 ? (totalProfit / totalStake) * 100 : 0;
    const winRate = closedBets.length > 0 ? (wonBets.length / closedBets.length) * 100 : 0;

    // Best win streak
    let bestStreak = 0;
    let cur = 0;
    for (const b of [...bets].reverse()) {
      if (b.result === 'won') { cur = cur >= 0 ? cur + 1 : 1; }
      else if (b.result === 'lost') { cur = 0; }
      if (cur > bestStreak) bestStreak = cur;
    }

    // Unique bookmakers
    const uniqueBookmakers = new Set(bets.map((b) => b.bookmaker)).size;

    // High odds bet (>= 3.0)
    const highOddsBets = bets.filter((b) => b.odds >= 3.0 && b.result === 'won').length;

    return [
      {
        id: 'first_bet',
        label: 'Primera Apuesta',
        description: 'Registra tu primera apuesta',
        icon: 'ri-flag-line',
        color: '#00e87a',
        unlocked: bets.length >= 1,
        progress: Math.min(bets.length, 1),
        progressMax: 1,
      },
      {
        id: 'ten_bets',
        label: 'Apostador Activo',
        description: 'Registra 10 apuestas',
        icon: 'ri-stack-line',
        color: '#00e87a',
        unlocked: bets.length >= 10,
        progress: Math.min(bets.length, 10),
        progressMax: 10,
      },
      {
        id: 'fifty_bets',
        label: 'Veterano',
        description: 'Registra 50 apuestas',
        icon: 'ri-medal-line',
        color: '#ffd700',
        unlocked: bets.length >= 50,
        progress: Math.min(bets.length, 50),
        progressMax: 50,
      },
      {
        id: 'hundred_bets',
        label: 'Centenario',
        description: 'Registra 100 apuestas',
        icon: 'ri-trophy-line',
        color: '#ffd700',
        unlocked: bets.length >= 100,
        progress: Math.min(bets.length, 100),
        progressMax: 100,
      },
      {
        id: 'positive_roi',
        label: 'ROI Positivo',
        description: 'Consigue un ROI mayor al 0%',
        icon: 'ri-line-chart-line',
        color: '#00e87a',
        unlocked: roi > 0 && closedBets.length >= 5,
      },
      {
        id: 'roi_10',
        label: 'Rentable',
        description: 'Consigue un ROI mayor al 10%',
        icon: 'ri-funds-line',
        color: '#ffd700',
        unlocked: roi >= 10 && closedBets.length >= 10,
      },
      {
        id: 'roi_20',
        label: 'Experto',
        description: 'Consigue un ROI mayor al 20%',
        icon: 'ri-vip-crown-line',
        color: '#ff6b35',
        unlocked: roi >= 20 && closedBets.length >= 20,
      },
      {
        id: 'winrate_60',
        label: 'Buen Ojo',
        description: 'Supera el 60% de acierto (mín. 10 apuestas)',
        icon: 'ri-eye-line',
        color: '#00e87a',
        unlocked: winRate >= 60 && closedBets.length >= 10,
      },
      {
        id: 'winrate_70',
        label: 'Ojo de Águila',
        description: 'Supera el 70% de acierto (mín. 20 apuestas)',
        icon: 'ri-focus-3-line',
        color: '#ffd700',
        unlocked: winRate >= 70 && closedBets.length >= 20,
      },
      {
        id: 'streak_3',
        label: 'Racha Caliente',
        description: '3 apuestas ganadas seguidas',
        icon: 'ri-fire-line',
        color: '#ff6b35',
        unlocked: bestStreak >= 3,
        progress: Math.min(bestStreak, 3),
        progressMax: 3,
      },
      {
        id: 'streak_7',
        label: 'En Llamas',
        description: '7 apuestas ganadas seguidas',
        icon: 'ri-fire-fill',
        color: '#ff6b35',
        unlocked: bestStreak >= 7,
        progress: Math.min(bestStreak, 7),
        progressMax: 7,
      },
      {
        id: 'login_7',
        label: '7 Días Seguidos',
        description: 'Inicia sesión 7 días consecutivos',
        icon: 'ri-calendar-check-line',
        color: '#00e87a',
        unlocked: (streak?.longest_streak ?? 0) >= 7,
        progress: Math.min(streak?.current_streak ?? 0, 7),
        progressMax: 7,
      },
      {
        id: 'login_30',
        label: 'Mes Completo',
        description: 'Inicia sesión 30 días consecutivos',
        icon: 'ri-calendar-2-line',
        color: '#ffd700',
        unlocked: (streak?.longest_streak ?? 0) >= 30,
        progress: Math.min(streak?.current_streak ?? 0, 30),
        progressMax: 30,
      },
      {
        id: 'multi_bookmaker',
        label: 'Diversificado',
        description: 'Usa 3 casas de apuestas distintas',
        icon: 'ri-store-2-line',
        color: '#a8b2c1',
        unlocked: uniqueBookmakers >= 3,
        progress: Math.min(uniqueBookmakers, 3),
        progressMax: 3,
      },
      {
        id: 'high_odds',
        label: 'Cazador de Cuotas',
        description: 'Gana 3 apuestas con cuota &gt;= 3.0',
        icon: 'ri-price-tag-3-line',
        color: '#ffd700',
        unlocked: highOddsBets >= 3,
        progress: Math.min(highOddsBets, 3),
        progressMax: 3,
      },
      {
        id: 'first_post',
        label: 'Primer Pick',
        description: 'Publica tu primer post en la comunidad',
        icon: 'ri-article-line',
        color: '#00e87a',
        unlocked: postsCount >= 1,
        progress: Math.min(postsCount, 1),
        progressMax: 1,
      },
    ];
  }, [bets, streak, postsCount]);

  const unlocked = achievements.filter((a) => a.unlocked);
  const locked = achievements.filter((a) => !a.unlocked);

  return (
    <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-white font-bold text-base">Logros y Badges</h3>
          <p className="text-[#6b7280] text-xs mt-0.5">
            {unlocked.length} / {achievements.length} desbloqueados
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#ffd700]/10">
            <i className="ri-award-line text-[#ffd700] text-base"></i>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="h-1.5 bg-[#2a2d3a] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#00e87a] to-[#ffd700] rounded-full transition-all duration-700"
            style={{ width: `${(unlocked.length / achievements.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Unlocked badges */}
      {unlocked.length > 0 && (
        <div className="mb-5">
          <p className="text-[#6b7280] text-xs font-medium uppercase tracking-wider mb-3">Desbloqueados</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {unlocked.map((a) => (
              <div
                key={a.id}
                className="relative flex flex-col items-center gap-2 p-3 rounded-xl border transition-all"
                style={{ borderColor: `${a.color}30`, background: `${a.color}08` }}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-full"
                  style={{ background: `${a.color}20`, color: a.color }}
                >
                  <i className={`${a.icon} text-lg`}></i>
                </div>
                <div className="text-center">
                  <p className="text-white text-xs font-bold leading-tight">{a.label}</p>
                  <p className="text-[#6b7280] text-xs mt-0.5 leading-tight">{a.description}</p>
                </div>
                <div
                  className="absolute top-2 right-2 w-4 h-4 flex items-center justify-center rounded-full"
                  style={{ background: `${a.color}30`, color: a.color }}
                >
                  <i className="ri-check-line" style={{ fontSize: '10px' }}></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Locked badges */}
      {locked.length > 0 && (
        <div>
          <p className="text-[#6b7280] text-xs font-medium uppercase tracking-wider mb-3">Por desbloquear</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {locked.map((a) => (
              <div
                key={a.id}
                className="flex flex-col items-center gap-2 p-3 rounded-xl border border-[#2a2d3a] opacity-50"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#2a2d3a] text-[#6b7280]">
                  <i className={`${a.icon} text-lg`}></i>
                </div>
                <div className="text-center w-full">
                  <p className="text-[#a8b2c1] text-xs font-bold leading-tight">{a.label}</p>
                  <p className="text-[#6b7280] text-xs mt-0.5 leading-tight">{a.description}</p>
                  {a.progressMax && a.progress !== undefined && (
                    <div className="mt-2">
                      <div className="h-1 bg-[#2a2d3a] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${(a.progress / a.progressMax) * 100}%`,
                            background: a.color,
                          }}
                        />
                      </div>
                      <p className="text-[#6b7280] text-xs mt-1">{a.progress}/{a.progressMax}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {bets.length === 0 && postsCount === 0 && (
        <div className="text-center py-6">
          <p className="text-[#6b7280] text-sm">Registra apuestas y publica posts para desbloquear logros</p>
        </div>
      )}
    </div>
  );
}
